import { Select, Stack } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import {
  IFormInputContext,
  IFormInputSpec,
  IFormInputSpecComparatorOperator,
} from 'pages/revisions-module/template-editor/editors/form/types';
import { useEffect, useMemo } from 'react';

interface ComparatorInputPropertiesSubFormProps {
  form: UseFormReturnType<IFormInputSpec>;
  context: IFormInputContext;
}

const OPERATOR_OPTIONS: { value: IFormInputSpecComparatorOperator; label: string; group?: string }[] = [
  { value: 'empty', label: 'je prázdne', group: 'Obsahové' },
  { value: 'not-empty', label: 'nie je prázdne', group: 'Obsahové' },
  { value: 'eq', label: '=', group: 'Porovnávacie' },
  { value: 'ne', label: '≠', group: 'Porovnávacie' },
  { value: 'gt', label: '>', group: 'Porovnávacie' },
  { value: 'lt', label: '<', group: 'Porovnávacie' },
  { value: 'gte', label: '≥', group: 'Porovnávacie' },
  { value: 'lte', label: '≤', group: 'Porovnávacie' },
  { value: 'logical-and', label: 'a zároveň', group: 'Logické' },
  { value: 'logical-or', label: 'alebo', group: 'Logické' },
  { value: 'logical-not', label: 'negácia', group: 'Logické' },
];

/**
 * Property editor for the comparator input type.
 */
export default function ComparatorInputPropertiesSubForm({ form, context }: ComparatorInputPropertiesSubFormProps) {
  const { allInputs } = useFormInputs();

  const inputOptions = useMemo(
    () =>
      allInputs
        .filter((input) => input.context === context && input.spec.name !== form.values.name)
        .map(({ spec }) => ({ value: spec.name, label: spec.label })),
    [allInputs, context, form.values.name]
  );

  const operator = (form.values as IFormInputSpec & { operator?: IFormInputSpecComparatorOperator }).operator;
  const isUnary = operator === 'empty' || operator === 'not-empty' || operator === 'logical-not';

  useEffect(() => {
    if (isUnary && form.values.type === 'comparator') {
      form.setFieldValue('fieldB', '');
    }
  }, [isUnary, form.values.type]);

  return (
    <Stack spacing={12}>
      <Select
        required
        label="Pole A"
        placeholder={inputOptions.length === 0 ? 'Najprv vytvorte iné pole v tejto sekcii' : undefined}
        searchable
        data={inputOptions}
        nothingFound="Žiadne pole"
        disabled={inputOptions.length === 0}
        {...form.getInputProps('fieldA')}
      />
      <Select required label="Operátor" data={OPERATOR_OPTIONS} {...form.getInputProps('operator')} />
      {!isUnary && (
        <Select
          required
          label="Pole B"
          placeholder={inputOptions.length === 0 ? 'Najprv vytvorte iné pole v tejto sekcii' : undefined}
          searchable
          data={inputOptions}
          nothingFound="Žiadne pole"
          disabled={inputOptions.length === 0}
          {...form.getInputProps('fieldB')}
        />
      )}
    </Stack>
  );
}
